import { getConfigurationSpecific } from "../apollo/server";
import { SERVER_URL } from "./global";
import { DAYS } from "./constantValues";
import { dayKeyOf, restaurantToday, timeIn, wallParts, zonedWallTimeToUtc } from "./restaurantClock";

// Window (minutes) for the "closing soon" / "opening soon" warning states.
const SOON_WINDOW = 60;

// Compute a restaurant's live open status from its openingTimes
// ([{ day:"MON", times:[{ startTime:[h,m], endTime:[h,m] }] }]).
// Returns { status, label } where status is one of:
//   'open' | 'closing-soon' | 'closed' | 'opening-soon'
// `tz` is the restaurant's time zone: its hours are read on its clock, not the
// customer's (utils/restaurantClock.js). Without it, this browser's clock.
export const getOpenStatus = (openingTimes, tz = null) => {
  if (!Array.isArray(openingTimes) || openingTimes.length === 0) {
    return { status: 'closed', label: 'Closed' };
  }
  const wall = wallParts(new Date(), tz);
  const nowMins = wall.minutes;
  const today = openingTimes.find((o) => o.day === DAYS[wall.dayIdx]);
  const toMins = (t) => Number(t[0]) * 60 + Number(t[1]);

  if (today && Array.isArray(today.times)) {
    // Currently within a window?
    for (const t of today.times) {
      const start = toMins(t.startTime);
      const end = toMins(t.endTime);
      if (nowMins >= start && nowMins <= end) {
        return end - nowMins <= SOON_WINDOW
          ? { status: 'closing-soon', label: 'Closes soon' }
          : { status: 'open', label: 'Open' };
      }
    }
    // Opening later today within the soon window?
    const upcoming = today.times
      .map((t) => toMins(t.startTime))
      .filter((start) => start > nowMins)
      .sort((a, b) => a - b)[0];
    if (upcoming != null && upcoming - nowMins <= SOON_WINDOW) {
      return { status: 'opening-soon', label: 'Opens soon' };
    }
  }
  return { status: 'closed', label: 'Closed' };
};

// When a restaurant next opens, from its hours: "11:00 AM" (today),
// "tomorrow 11:00 AM", "Fri 11:00 AM" — on the restaurant's clock, with its
// zone added when that isn't the customer's ("11:00 AM CST"). Null when its
// hours don't say.
export const nextOpeningLabel = (openingTimes, tz = null) => {
  if (!Array.isArray(openingTimes) || openingTimes.length === 0) return null;
  const now = new Date();
  const today = restaurantToday(tz, now);
  for (let i = 0; i < 8; i++) {
    const day = new Date(today);
    day.setDate(today.getDate() + i);
    const entry = openingTimes.find((o) => o.day === DAYS[day.getDay()]);
    const starts = (entry?.times || [])
      .map((t) => Number(t.startTime[0]) * 60 + Number(t.startTime[1]))
      .sort((a, b) => a - b);
    for (const mins of starts) {
      const at = zonedWallTimeToUtc(dayKeyOf(day), mins, tz);
      if (at <= now) continue;
      if (i === 0) return timeIn(at, tz);
      if (i === 1) return `tomorrow ${timeIn(at, tz)}`;
      return `${day.toLocaleDateString([], { weekday: 'short' })} ${timeIn(at, tz)}`;
    }
  }
  return null;
};

// A restaurant card's open/closed status. A manual isAvailable=false (vendor
// toggled off) always wins → Closed regardless of hours. A food truck is
// authoritative from the server: its hours ARE its stop windows, and only the
// backend knows which stop is live right now.
export const restaurantOpenStatus = (item) => {
  if (item?.isAvailable === false) return { status: 'closed', label: 'Closed' };
  if (item?.isMobile === true) {
    return { status: item.isOpenNow ? 'open' : 'closed', label: item.isOpenNow ? 'Open' : 'Closed' };
  }
  return getOpenStatus(item?.openingTimes, item?.timezone);
};

// Taking orders right now ("opens soon" does not count).
export const isRestaurantOpenNow = (item) => ['open', 'closing-soon'].includes(restaurantOpenStatus(item).status);

// Shape callers actually destructure ({ webAmplitudeApiKey }) — the old fallback
// nested it under `configuration`, so it never matched and always read undefined.
const FALLBACK_CONFIGURATION = { webAmplitudeApiKey: "" };

export const fetchConfiguration = async () => {
  try {
    const response = await fetch(`${SERVER_URL}graphql`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // Add any necessary authentication headers here
      },
      body: JSON.stringify({
        query: getConfigurationSpecific,
      }),
    });

    const result = await response.json();
    // A GraphQL error still answers HTTP 200 with data:null, so `ok` alone is no
    // guarantee the configuration is there. Callers destructure this result, and
    // destructuring undefined throws — always hand back an object.
    if (response.ok) {
      return result.data?.configuration || FALLBACK_CONFIGURATION;
    }
    return FALLBACK_CONFIGURATION;
  } catch (err) {
    return FALLBACK_CONFIGURATION;
  }
};
